import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestPayLetter } from '@pocapan/shared/domains/orders/services/orderServices';
import { usePayment } from './usePayment';
import type { PayLetterPaymentMethod } from '@pocapan/shared/domains/orders/services/orderServices';
import type { OrderProductType } from '../../../domain/repositories/PaymentRepository';

interface UsePayLetterPaymentProps {
  amount: string;
  productName: string;
  productId: string;
  productType?: OrderProductType;
  sceneId?: string;
  autoPay?: boolean;
  registrationOnly?: boolean;
}

interface PayLetterPaymentRequestSuccessResponse {
  token: number;
  online_url?: string;
  mobile_url: string;
}

const isPayLetterPaymentRequestSuccessResponse = (
  body: unknown
): body is PayLetterPaymentRequestSuccessResponse => {
  return (
    !!body &&
    typeof body === 'object' &&
    'mobile_url' in body &&
    typeof body.mobile_url === 'string' &&
    body.mobile_url.length > 0
  );
};

export const usePayLetterPayment = ({
  amount,
  productName: _productName,
  productId,
  productType,
  sceneId,
  autoPay = false,
  registrationOnly = false
}: UsePayLetterPaymentProps) => {
  const navigate = useNavigate();
  const { generateOrderId } = usePayment();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const handleCreateOrder = useCallback(
    async (paymentMethod?: PayLetterPaymentMethod) => {
      if (!productId || isSubmittingRef.current) return;

      try {
        isSubmittingRef.current = true;
        setIsSubmitting(true);
        const parsedAmount = Number(amount);
        if (!registrationOnly && (!Number.isFinite(parsedAmount) || parsedAmount <= 0)) {
          throw new Error('유효한 결제 금액이 아닙니다.');
        }

        const orderId = await generateOrderId(productId, productType, sceneId);
        const responseBody = await requestPayLetter(orderId, paymentMethod, {
          autopay: autoPay,
          registration_only: registrationOnly
        });

        if (!isPayLetterPaymentRequestSuccessResponse(responseBody)) {
          console.log('PayLetter request missing mobile_url', { body: responseBody });
          throw new Error('PayLetter 결제 URL을 받지 못했습니다.');
        }

        window.location.replace(responseBody.mobile_url);
      } catch (error) {
        console.log('PayLetter payment request failed', error);
        let reason = 'fe-request';
        if (error instanceof Error && error.message) {
          reason = encodeURIComponent(error.message.slice(0, 120));
        }
        navigate(`/payment/fail?provider=pay-letter&reason=${reason}`, { replace: true });
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    [amount, autoPay, generateOrderId, navigate, productId, productType, registrationOnly, sceneId]
  );

  return { handleCreateOrder, isSubmitting };
};
